// v10/src/components/AircraftSelector.js - REDESIGNED + OFFLINE CACHE + SQUAWK AOG + i18n
import React, { useState, useEffect, useMemo, memo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Loader, AlertCircle, RefreshCw, Database, MapPin, CheckCircle, CloudOff, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { aircraftService } from '../services/apiService';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { useToast } from '../hooks/useToast';
import { getAudioCacheInfo, fetchAndStoreManifest } from '../utils/speechHelper';
import { preCachePreLandingAudio } from '../utils/speechHelper';
import { POLLING_INTERVALS } from '../config/realtime';
import useIsMobile from '../hooks/useIsMobile';
import { transformChecklistFromBackend } from '../utils/checklist';

const getMfrIcon = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('piper'))   return '🛩';
  if (n.includes('cessna'))  return '✈️';
  if (n.includes('beech'))   return '🛫';
  if (n.includes('diamond')) return '💎';
  if (n.includes('cirrus'))  return '🚀';
  return '✈️';
};

const getMfrColor = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('piper'))   return ['#1d4ed8','#6d28d9'];
  if (n.includes('cessna'))  return ['#0369a1','#0891b2'];
  if (n.includes('beech'))   return ['#7c3aed','#a21caf'];
  if (n.includes('diamond')) return ['#0f766e','#0369a1'];
  if (n.includes('cirrus'))  return ['#dc2626','#ea580c'];
  return ['#2563eb','#7c3aed'];
};

// ── AircraftCard ──────────────────────────────────────────────────
const AircraftCard = ({ model, selected, onClick, index, userRole, isGrounded, isMel }) => {
  const { t } = useTranslation();
  const [c1, c2] = getMfrColor(model.manufacturer);
  const icon      = getMfrIcon(model.manufacturer);
  const [cacheInfo, setCacheInfo] = React.useState({ cached: false, count: 0, offlineCount: 0, updatedAt: null });
  React.useEffect(() => { getAudioCacheInfo(model.id).then(setCacheInfo); }, [model.id]);
  const isStaff   = userRole === 'admin' || userRole === 'instructor';

  const hobbsBadge = isStaff && model.hobbs_current > 0 ? (() => {
    const flown    = (parseFloat(model.hobbs_current) || 0) - (parseFloat(model.hobbs_last_service) || 0);
    const interval = parseFloat(model.service_interval_hours) || 100;
    const rem      = Math.round((interval - flown) * 10) / 10;
    if (rem <= 0)  return { text: t('service_overdue'),                       color: '#ef4444', bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.3)',  icon: '🚨' };
    if (rem <= 10) return { text: t('service_soon', { hours: rem }),          color: '#d97706', bg: 'rgba(217,119,6,0.12)', border: 'rgba(217,119,6,0.3)',  icon: '⚠️' };
    return           { text: t('service_remaining', { hours: rem }),          color: '#16a34a', bg: 'rgba(22,163,74,0.1)',  border: 'rgba(22,163,74,0.25)', icon: '✅' };
  })() : null;

  const hasAudio = cacheInfo.cached && cacheInfo.count > 0;

  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, type: 'spring', stiffness: 200, damping: 20 }}
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.97 }}
      style={{
        width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
        borderRadius: '18px', padding: '14px 16px',
        background: isGrounded
          ? 'rgba(239,68,68,0.04)'
          : selected
            ? `linear-gradient(135deg, ${c1}18, ${c2}10)`
            : 'rgba(255,255,255,0.7)',
        boxShadow: isGrounded
          ? '0 2px 8px rgba(239,68,68,0.1), 0 0 0 1.5px rgba(239,68,68,0.2)'
          : selected
            ? `0 4px 20px ${c1}25, 0 0 0 2px ${c1}40`
            : '0 2px 8px rgba(0,0,0,0.05), 0 0 0 1.5px rgba(37,99,235,0.08)',
        transition: 'box-shadow 0.2s ease, background 0.2s ease',
        display: 'flex', alignItems: 'center', gap: '14px',
        backdropFilter: 'blur(8px)',
      }}
    >
      <div style={{
        width: '48px', height: '48px', borderRadius: '14px', flexShrink: 0,
        background: isGrounded ? 'linear-gradient(135deg,#ef4444,#dc2626)' : `linear-gradient(135deg, ${c1}, ${c2})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '22px', boxShadow: isGrounded ? '0 4px 12px rgba(239,68,68,0.35)' : `0 4px 12px ${c1}35`,
      }}>
        {isGrounded ? '⛔' : icon}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          margin: 0, fontWeight: 800, fontSize: '15px',
          color: isGrounded ? '#ef4444' : selected ? c1 : '#0f172a',
          letterSpacing: '-0.2px', lineHeight: 1.2, transition: 'color 0.2s',
        }}>
          {model.name}
        </p>
        {model.registration && (
          <p style={{ margin: '3px 0 0', fontSize: '12px', fontWeight: 600, color: '#64748b', fontFamily: 'monospace', letterSpacing: '0.06em' }}>
            {model.registration}
          </p>
        )}

        {isGrounded && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '4px' }}>
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#ef4444', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', padding: '2px 8px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
              {t('aog_label')}
            </span>
          </div>
        )}

        {!isGrounded && isMel && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '4px' }}>
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#d97706', background: 'rgba(217,119,6,0.1)', border: '1px solid rgba(217,119,6,0.25)', padding: '2px 8px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
              {t('mel_label')}
            </span>
          </div>
        )}

        {!isGrounded && !isMel && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '4px' }}>
            <span style={{ fontSize: '10px', fontWeight: 700, color: '#16a34a', background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.25)', padding: '2px 8px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
              {t('operational_label')}
            </span>
          </div>
        )}

        {!isGrounded && hobbsBadge && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '4px' }}>
            <span style={{ fontSize: '10px', fontWeight: 700, color: hobbsBadge.color, background: hobbsBadge.bg, border: `1px solid ${hobbsBadge.border}`, padding: '2px 8px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
              {hobbsBadge.icon} {hobbsBadge.text}
            </span>
          </div>
        )}

        {(model.fuel_capacity_liters || model.oil_capacity_quarts) && (
          <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
            {model.fuel_capacity_liters && (
              <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 500 }}>⛽ {model.fuel_capacity_liters}L</span>
            )}
            {model.oil_capacity_quarts && (
              <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 500 }}>🛢 {model.oil_capacity_quarts}qt</span>
            )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300 }}
            style={{
              width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
              background: isGrounded ? 'linear-gradient(135deg,#ef4444,#dc2626)' : `linear-gradient(135deg, ${c1}, ${c2})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 3px 10px ${isGrounded ? 'rgba(239,68,68,0.4)' : c1 + '40'}`,
            }}
          >
            <CheckCircle style={{ width: '16px', height: '16px', color: 'white' }} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
};

// ── DebugPanel (temporal) ─────────────────────────────────────────
const DebugPanel = ({ selectedAircraftId }) => {
  const [visible, setVisible] = useState(() => localStorage.getItem('condor360_debug') === 'true');
  const [info, setInfo] = useState({ online: navigator.onLine, manifestKeys: 0, cacheEntries: 0 });

  useEffect(() => {
    if (!visible) return;
    const update = async () => {
      let manifestKeys = 0;
      let cacheEntries = 0;
      try {
        if (selectedAircraftId) {
          const raw = localStorage.getItem(`condor360_audio_manifest_${selectedAircraftId}`);
          if (raw) manifestKeys = Object.keys(JSON.parse(raw)).length;
        }
      } catch {}
      try {
        if ('caches' in window) {
          const cache = await caches.open('condor360-checklist-audio');
          const keys = await cache.keys();
          cacheEntries = keys.length;
        }
      } catch {}
      setInfo({ online: navigator.onLine, manifestKeys, cacheEntries });
    };
    update();
    const id = setInterval(update, 2000);
    return () => clearInterval(id);
  }, [visible, selectedAircraftId]);

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 99999,
      background: 'rgba(15,23,42,0.88)', backdropFilter: 'blur(8px)',
      color: '#e2e8f0', fontSize: '11px', fontFamily: 'monospace',
      padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '16px',
    }}>
      <span>navigator.onLine: <b style={{ color: info.online ? '#4ade80' : '#f87171' }}>{String(info.online)}</b></span>
      <span>manifest keys: <b>{info.manifestKeys}</b></span>
      <span>cache entries: <b>{info.cacheEntries}</b></span>
      <button onClick={() => { setVisible(false); localStorage.removeItem('condor360_debug'); }}
        style={{ marginLeft: 'auto', background: 'none', border: '1px solid rgba(255,255,255,0.25)', color: '#e2e8f0', borderRadius: '4px', cursor: 'pointer', padding: '2px 8px', fontSize: '11px', fontFamily: 'monospace' }}>
        X
      </button>
    </div>
  );
};

// ── AircraftSelector ──────────────────────────────────────────────
const AircraftSelector = memo(({ onSelectAircraft, onClubSubmit, userRole, preselectedAircraftId, onQrHandled, onPreLanding, orgFeatures, onUpsell, onStartFlow, onStartEmergency, currentUser }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const isMobile = useIsMobile();
  const [searchTerm, setSearchTerm]           = useState('');
  const [clubName, setClubName]               = useState(() => currentUser?.organization_name || 'Aeroclub Puerto Madryn');
  const [selectedAircraft, setSelected]       = useState(null);
  const [loadingGeo, setLoadingGeo]           = useState(true);
  const [submitting, setSubmitting]           = useState(false);
  const [cacheVersion, setCacheVersion]       = useState(0);
  const [cacheStatus, setCacheStatus]         = useState('');
  const [aircraftModels, setAircraftModels]   = useState([]);
  const [loadingAircraft, setLoadingAircraft] = useState(true);
  const [aircraftError, setAircraftError]     = useState(null);
  const [groundedAircraft, setGroundedAircraft] = useState(new Set());
  const [melAircraft, setMelAircraft] = useState(new Set());
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const pollGroundedRef = useRef(null);
  const mountedRef = useRef(true);

  const [installPrompt,     setInstallPrompt]     = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isInstalled,       setIsInstalled]       = useState(false);
  const [showManualTip,     setShowManualTip]     = useState(false);

  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;

  useEffect(() => {
    if (isStandalone) { setIsInstalled(true); return; }
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      if (!localStorage.getItem('condor360_install_dismissed')) setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => { setIsInstalled(true); setShowInstallBanner(false); });
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isStandalone]);

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') setIsInstalled(true);
      setInstallPrompt(null);
      setShowInstallBanner(false);
    } else {
      setShowManualTip(true);
      setShowInstallBanner(false);
    }
  };

  const handleDismissInstall = () => {
    setShowInstallBanner(false);
    localStorage.setItem('condor360_install_dismissed', '1');
  };

  const { isOnline, cacheReferenceImages } = useOfflineSync();

  const fetchGroundedStatus = useCallback(async (models) => {
    if (!isOnline || !models || !models.length) return;
    try {
      const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const stored = localStorage.getItem('condor360_current_user') || sessionStorage.getItem('condor360_current_user') || '{}';
      const token = JSON.parse(stored).token || '';
      if (!token) return; // Fix 1 - guard JWT
      const statuses = await Promise.all(
        models.map(a =>
          fetch(API + '/api/squawks/aircraft/' + a.id + '/status', {
            headers: { Authorization: 'Bearer ' + token }
          }).then(r => r.json()).catch(() => ({ is_grounded: false }))
        )
      );
      if (!mountedRef.current) return; // ✅ Fix 2 — no setState si desmontado
      const grounded = new Set(
        models.filter((_, i) => statuses[i] && statuses[i].is_grounded).map(a => a.id)
      );
      const mel = new Set(
        models.filter((_, i) => statuses[i] && statuses[i].has_mel && !statuses[i].is_grounded).map(a => a.id)
      );
      setGroundedAircraft(grounded);
      setMelAircraft(mel);
    } catch (_) {}
    // ✅ Fix 3 — cleanup del intervalo acá, no en el useEffect de eventos
    return () => { if (pollGroundedRef.current) clearInterval(pollGroundedRef.current); };
  }, [isOnline]); // eslint-disable-line

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingAircraft(true); setAircraftError(null);
        const response = await aircraftService.getAll({ is_active: true });
        if (response.data.success && response.data.data) {
          const models = response.data.data.map(a => ({
            id: a.id, name: `${a.manufacturer} ${a.model_name}`,
            manufacturer: a.manufacturer, model_name: a.model_name,
            registration: a.registration,
            fuel_capacity_liters: a.fuel_capacity_liters,
            oil_capacity_quarts: a.oil_capacity_quarts,
            hobbs_current: a.hobbs_current,
            hobbs_last_service: a.hobbs_last_service,
            service_interval_hours: a.service_interval_hours,
            checklist: [],
          }));
          setAircraftModels(models);
          fetchGroundedStatus(models);
          if (pollGroundedRef.current) clearInterval(pollGroundedRef.current);
          pollGroundedRef.current = setInterval(() => fetchGroundedStatus(models), POLLING_INTERVALS.aircraft); 
          if (preselectedAircraftId) {
            const preselected = models.find(m => m.id === preselectedAircraftId);
            if (preselected) {
              setSelected(preselected);
              if (onQrHandled) onQrHandled();
              const url = new URL(window.location.href);
              url.searchParams.delete('aircraft');
              window.history.replaceState({}, '', url.toString());
            }
          }
        }
      } catch (err) {
        setAircraftError(t('aircraft_load_error'));
      } finally { setLoadingAircraft(false); }
    };
    load();
  }, [isOnline]);

  useEffect(() => {
    // Rehabilitar el ref al ejecutar el effect: protege el caso en que el
    // mismo instance se reusa (StrictMode dev double-invoke o cualquier
    // remount donde useRef no se reinicialice) y el cleanup previo dejó
    // mountedRef.current=false, lo que silenciaría los setState de abajo
    // y dejaría loadingGeo colgado / clubName vacío.
    mountedRef.current = true;
    // Fallback unificado para los caminos de geo: nunca pisar con '' lo que ya
    // tenía clubName (ej.: org del usuario o lo que tipeó a mano antes de que
    // resuelva la geo). Orden: valor previo no vacío → organization_name del
    // user → mismo default que usa el useState inicial.
    const fallbackClubName = (prev) =>
      prev || currentUser?.organization_name || 'Aeroclub Puerto Madryn';
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          if (!mountedRef.current) return;
          const { latitude: lat, longitude: lon } = pos.coords;
          try {
            const API   = process.env.REACT_APP_API_URL || 'http://localhost:5000';
            const token = (() => {
              try {
                const u = JSON.parse(
                  localStorage.getItem('condor360_current_user') ||
                  sessionStorage.getItem('condor360_current_user') || '{}'
                );
                return u.token || '';
              } catch { return ''; }
            })();
            const res  = await fetch(`${API}/api/airports/nearest?lat=${lat}&lng=${lon}&radius=15`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (mountedRef.current) {
              if (data.success && data.found && data.data?.name) {
                setClubName(data.data.name);
              } else {
                setClubName(fallbackClubName);
              }
            }
          } catch {
            if (mountedRef.current) setClubName(fallbackClubName);
          } finally {
            if (mountedRef.current) setLoadingGeo(false);
          }
        },
        () => { setClubName(fallbackClubName); setLoadingGeo(false); },
        { timeout: 6000 }
      );
    } else {
      setClubName(fallbackClubName);
      setLoadingGeo(false);
    }
    return () => { mountedRef.current = false; };
  }, []);

  const handleSelectAircraft = async (model) => {
    setSelected(model);
    if (!isOnline) { setCacheStatus('offline'); return; }
    setCacheStatus('caching');
    try {
      await cacheReferenceImages(model.id);
      await fetchAndStoreManifest(model.id);
      if (orgFeatures?.pre_landing !== false) {
        const PRE_LANDING_VOICES = [
          { voice: 'Mil doscientos pies. Reducir potencia a dos mil RPM.' },
          { voice: 'Aplicar aire caliente al carburador y encender bomba de combustible.' },
          { voice: 'Flaps, primer punto.' },
          { voice: 'Avisame cuando estés en la recta final.' },
          { voice: 'Luces encendidas y flaps segundo punto. Listo para aterrizar.' },
        ];
        preCachePreLandingAudio(PRE_LANDING_VOICES);
      }
      setCacheVersion(v => v + 1);
      setCacheStatus('cached');
      setTimeout(() => setCacheStatus(''), 3000);
    } catch { setCacheStatus(''); }
  };

  useEffect(() => {
    const handleRefresh = () => {
      if (aircraftModels.length > 0) fetchGroundedStatus(aircraftModels);
    };
    window.addEventListener('condor360:refresh', handleRefresh);
    window.addEventListener('condor360:squawk-updated', handleRefresh);
    return () => {
      // ✅ Fix 4 — solo limpiar listeners, nunca el intervalo acá
      window.removeEventListener('condor360:refresh', handleRefresh);
      window.removeEventListener('condor360:squawk-updated', handleRefresh);
    };
  }, [aircraftModels, fetchGroundedStatus]);

  const filteredModels = useMemo(() =>
    aircraftModels.filter(m => m.name.toLowerCase().includes(searchTerm.toLowerCase())),
    [searchTerm, aircraftModels]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clubName || !selectedAircraft) return;
    setSubmitting(true);
    try {
      const currentLang = localStorage.getItem('condor360_language') || 'es-AR';
      const langParam = currentLang === 'en' ? 'en' : 'es';
      // CANDADO ETAPA 2: el piloto solo ejecuta prevuelo. Se pide la fase
      // explícita para no heredar ítems de otras fases que el admin cargue
      // en el editor multi-fase (Etapa 3 habilitará ejecutar otras fases).
      const res = await aircraftService.getById(selectedAircraft.id, langParam, 'prevuelo');
      if (res.data.success && res.data.data) {
        onClubSubmit({
          clubName,
          aircraft: {
            ...selectedAircraft,
            checklist: transformChecklistFromBackend(res.data.data.checklist)
          },
          // Multi-fase (Sub-etapa 3.2b): fases que este avión tiene
          // configuradas de verdad (3.2a). App.js arma el plan de fases a
          // ejecutar a partir de esto — NUNCA asumir las 3 por default acá.
          phasesAvailable: res.data.data.phases_available || ['prevuelo'],
        });
      } else { toast.error('Error al cargar el checklist del avión'); }
    } catch (err) {
      toast.error('Error al cargar los detalles del avión. Verificá la conexión con el backend.');
    } finally { setSubmitting(false); }
  };

  const isGroundedSelected = selectedAircraft && groundedAircraft.has(selectedAircraft.id);
  const canSubmit = !!clubName && !!selectedAircraft && !submitting && !isGroundedSelected;
  const [c1, c2]  = selectedAircraft ? getMfrColor(selectedAircraft.manufacturer) : ['#f97316','#ef4444'];

  if (loadingAircraft) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        style={{ maxWidth: '460px', width: '100%', margin: '0 auto', borderRadius: '28px', overflow: 'hidden', background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(20px)', boxShadow: '0 20px 60px rgba(37,99,235,0.12), 0 0 0 1px rgba(37,99,235,0.1)' }}>
        <div style={{ padding: '60px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
            style={{ width: '52px', height: '52px', borderRadius: '50%', border: '3px solid transparent', borderTopColor: '#2563eb', borderRightColor: '#7c3aed' }} />
          <p style={{ margin: 0, fontWeight: 700, fontSize: '15px', color: '#64748b' }}>{t('loading_fleet')}</p>
        </div>
      </motion.div>
    );
  }

  if (aircraftError) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        style={{ maxWidth: '460px', width: '100%', margin: '0 auto', borderRadius: '28px', background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(20px)', boxShadow: '0 20px 60px rgba(239,68,68,0.1), 0 0 0 1px rgba(239,68,68,0.12)', padding: '48px 32px', textAlign: 'center' }}>
        <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(239,68,68,0.08)', border: '2px solid rgba(239,68,68,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <AlertCircle style={{ width: '28px', height: '28px', color: '#ef4444' }} />
        </div>
        <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 800, color: '#dc2626' }}>{t('connection_error')}</h3>
        <p style={{ margin: '0 0 24px', fontSize: '14px', color: '#64748b', lineHeight: 1.5 }}>{aircraftError}</p>
        <motion.button onClick={() => window.location.reload()} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
          style={{ padding: '12px 28px', background: 'linear-gradient(135deg,#2563eb,#6d28d9)', color: 'white', border: 'none', borderRadius: '14px', fontWeight: 700, fontSize: '14px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 16px rgba(37,99,235,0.3)' }}>
          <RefreshCw style={{ width: '15px', height: '15px' }} /> {t('retry')}
        </motion.button>
      </motion.div>
    );
  }

  if (aircraftModels.length === 0) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        style={{ maxWidth: '460px', width: '100%', margin: '0 auto', borderRadius: '28px', background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(20px)', boxShadow: '0 20px 60px rgba(0,0,0,0.08)', padding: '48px 32px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>✈️</div>
        <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>{t('no_aircraft_title')}</h3>
        <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.6 }}>{t('no_aircraft_desc')}</p>
        <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
          <span style={{ fontSize: '12px', color: '#94a3b8' }}>{t('contact_admin')}</span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      data-tour="aircraft-selector"
      initial={{ opacity: 0, y: 30, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: 'spring', stiffness: 120, damping: 18 }}
      style={{
        maxWidth: '480px', width: '100%', margin: '0 auto',
        borderRadius: '28px', overflow: 'hidden',
        background: 'rgba(255,255,255,0.88)',
        backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
        boxShadow: '0 24px 80px rgba(37,99,235,0.12), 0 0 0 1px rgba(37,99,235,0.1)',
      }}
    >
      <div style={{ padding: '24px 24px 20px', background: 'linear-gradient(135deg,rgba(37,99,235,0.07),rgba(109,40,217,0.05))', borderBottom: '1px solid rgba(37,99,235,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <motion.div animate={{ x: [0,4,0], rotate: [0,5,0] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '52px' }}>
            ✈️
          </motion.div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'flex-start' : 'space-between', gap: '8px' }}>
              <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 900, color: '#0f172a', letterSpacing: '-0.5px' }}>
                {t('select_aircraft')}
              </h2>
              {!isInstalled && !isMobile && (
                <motion.button onClick={handleInstall} whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.94 }}
                  title="Instalar Condor360 en tu pantalla de inicio"
                  style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px', borderRadius: '20px', flexShrink: 0, background: 'rgba(37,99,235,0.07)', border: '1px solid rgba(37,99,235,0.18)', color: '#2563eb', cursor: 'pointer', fontSize: '11px', fontWeight: 700, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                  <span style={{ fontSize: '13px' }}>📲</span> {t('install')}
                </motion.button>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
              <p style={{ margin: 0, fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                {t('aircraft_available', { count: aircraftModels.length })}
                {groundedAircraft.size > 0 && (
                  <span style={{ marginLeft: '6px', color: '#ef4444', fontWeight: 700 }}>· {groundedAircraft.size} AOG 🔴</span>
                )}
              </p>
              {!isOnline && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: 700, color: '#f59e0b', background: 'rgba(245,158,11,0.1)', padding: '2px 7px', borderRadius: '20px', border: '1px solid rgba(245,158,11,0.25)' }}>
                  <CloudOff style={{ width: '10px', height: '10px' }} /> {t('no_connection')}
                </span>
              )}
            </div>
            {!isInstalled && isMobile && (
              <motion.button onClick={handleInstall} whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.94 }}
                title="Instalar Condor360 en tu pantalla de inicio"
                style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px', borderRadius: '20px', marginTop: '8px', background: 'rgba(37,99,235,0.07)', border: '1px solid rgba(37,99,235,0.18)', color: '#2563eb', cursor: 'pointer', fontSize: '11px', fontWeight: 700, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                <span style={{ fontSize: '13px' }}>📲</span> {t('install')}
              </motion.button>
            )}
          </div>
        </div>
        <AnimatePresence>
          {!isOnline && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
              style={{ marginTop: '12px', padding: '10px 12px', borderRadius: '12px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <span style={{ fontSize: '14px' }}>⚠️</span>
              <p style={{ margin: 0, fontSize: '12px', color: '#92400e', lineHeight: 1.5, fontWeight: 500 }}>
                {t('offline_warning')}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <form onSubmit={handleSubmit} style={{ padding: '20px 20px 24px' }}>
        <div style={{ position: 'relative', marginBottom: '16px' }}>
          <Search style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: '#94a3b8' }} />
          <input type="text" placeholder={t('search_placeholder')} value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '12px 14px 12px 40px', border: '1.5px solid rgba(37,99,235,0.14)', borderRadius: '14px', fontSize: '14px', fontWeight: 500, background: 'rgba(248,250,255,0.8)', color: '#0f172a', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.18s', fontFamily: 'inherit' }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '280px', overflowY: 'auto', marginBottom: '16px', paddingRight: '2px' }}>
          <AnimatePresence>
            {filteredModels.length === 0 ? (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                style={{ textAlign: 'center', padding: '24px 0', fontSize: '14px', color: '#94a3b8', fontWeight: 500 }}>
                {t('no_results')}
              </motion.p>
            ) : (
              filteredModels.map((model, idx) => (
                <AircraftCard
                  key={model.id + '-' + cacheVersion}
                  model={model}
                  selected={selectedAircraft?.id === model.id}
                  onClick={() => handleSelectAircraft(model)}
                  index={idx}
                  userRole={userRole}
                  isGrounded={groundedAircraft.has(model.id)}
                  isMel={melAircraft.has(model.id)}
                />
              ))
            )}
          </AnimatePresence>
        </div>

        <div style={{ height: '1px', background: 'rgba(37,99,235,0.08)', margin: '0 0 14px' }} />

        <div style={{ position: 'relative', marginBottom: '16px' }}>
          <MapPin style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '15px', height: '15px', color: loadingGeo ? '#f59e0b' : '#94a3b8' }} />
          {loadingGeo && (
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
              style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', width: '14px', height: '14px', borderRadius: '50%', border: '2px solid transparent', borderTopColor: '#f59e0b' }} />
          )}
          <input type="text" placeholder={loadingGeo ? t('detecting_location') : t('org_placeholder')} value={clubName}
            onChange={e => setClubName(e.target.value)} required
            style={{ width: '100%', padding: '12px 14px 12px 38px', border: '1.5px solid rgba(37,99,235,0.14)', borderRadius: '14px', fontSize: '14px', fontWeight: 500, background: 'rgba(248,250,255,0.8)', color: '#0f172a', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
        </div>

        {/* ── Botón principal ── */}
        {orgFeatures?.flow_mode && currentUser?.flow_mode_enabled && onStartFlow ? (
          <motion.button type="button" disabled={!canSubmit}
            onClick={() => canSubmit && onStartFlow(selectedAircraft)}
            whileHover={canSubmit ? { scale: 1.02, y: -1 } : {}} whileTap={canSubmit ? { scale: 0.97 } : {}}
            style={{ width: '100%', padding: '12px', background: canSubmit ? 'linear-gradient(135deg,#818cf8,#6d28d9)' : 'rgba(15,23,42,0.06)', color: canSubmit ? 'white' : '#94a3b8', border: 'none', borderRadius: '18px', fontWeight: 800, fontSize: '16px', cursor: canSubmit ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', boxShadow: canSubmit ? '0 8px 28px rgba(129,140,248,0.4)' : 'none', transition: 'background 0.3s ease, box-shadow 0.3s ease, color 0.2s ease' }}>
            <span style={{ fontSize: '18px' }}>🌀</span>Flow Mode
          </motion.button>
        ) : (
          <motion.button type="submit" disabled={!canSubmit}
            whileHover={canSubmit ? { scale: 1.02, y: -1 } : {}} whileTap={canSubmit ? { scale: 0.97 } : {}}
            style={{ width: '100%', padding: '12px', background: canSubmit ? `linear-gradient(135deg, ${c1}, ${c2})` : 'rgba(15,23,42,0.06)', color: canSubmit ? 'white' : '#94a3b8', border: 'none', borderRadius: '18px', fontWeight: 800, fontSize: '16px', cursor: canSubmit ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', boxShadow: canSubmit ? `0 8px 28px ${c1}40` : 'none', transition: 'background 0.3s ease, box-shadow 0.3s ease, color 0.2s ease' }}>
            {submitting ? (
              <><motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                style={{ width: '18px', height: '18px', borderRadius: '50%', border: '2.5px solid transparent', borderTopColor: 'white' }} />
                {t('loading_checklist')}</>
            ) : (
              <><span style={{ fontSize: '18px' }}>🛫</span>{t('start_checklist')}</>
            )}
          </motion.button>
        )}

        {/* ── Checklist aterrizaje ── */}
        <AnimatePresence>
          {selectedAircraft && !groundedAircraft.has(selectedAircraft?.id) && (
            <motion.div key="pre-landing-btn"
              initial={{ opacity:0, height:0, marginTop:0 }}
              animate={{ opacity:1, height:'auto', marginTop:8 }}
              exit={{ opacity:0, height:0, marginTop:0 }}
              transition={{ duration:0.2 }}>
              {orgFeatures?.pre_landing ? (
                <motion.button type="button" onClick={() => onPreLanding && onPreLanding(selectedAircraft)}
                  whileHover={{ scale:1.02 }} whileTap={{ scale:0.96 }}
                  style={{ width:'100%', padding:'12px', border:'none', borderRadius:16, cursor:'pointer', fontFamily:'inherit', background:'linear-gradient(135deg,#0891b2,#7c3aed)', color:'white', boxShadow:'0 4px 14px rgba(8,145,178,0.3)', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                  <span style={{ fontSize:16 }}>🛬</span>
                  <span style={{ fontSize:13, fontWeight:700 }}>{t('landing_checklist')}</span>
                </motion.button>
              ) : (userRole === 'admin' || userRole === 'instructor') ? (
                <motion.button type="button" onClick={() => onUpsell && onUpsell('pre_landing')}
                  whileHover={{ scale:1.02 }} whileTap={{ scale:0.96 }}
                  style={{ width:'100%', padding:'12px', border:'1px solid rgba(100,116,139,0.2)', borderRadius:16, cursor:'pointer', fontFamily:'inherit', background:'rgba(100,116,139,0.12)', color:'#94a3b8', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                  <span style={{ fontSize:16 }}>🔒</span>
                  <span style={{ fontSize:13, fontWeight:700 }}>{t('landing_checklist')}</span>
                </motion.button>
              ) : null}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Más opciones ── */}
        <AnimatePresence>
          {selectedAircraft && !groundedAircraft.has(selectedAircraft?.id) && (
            <motion.div key="more-options"
              initial={{ opacity:0, height:0, marginTop:0 }}
              animate={{ opacity:1, height:'auto', marginTop:8 }}
              exit={{ opacity:0, height:0, marginTop:0 }}
              transition={{ duration:0.2 }}>
              <button type="button" onClick={() => setShowMoreOptions(v => !v)}
                style={{ width:'100%', padding:'8px', background:'none', border:'none', cursor:'pointer', fontSize:12, fontWeight:600, color:'#94a3b8', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}>
                ··· {showMoreOptions ? t('less_options') : t('more_options')}
              </button>
              <AnimatePresence>
                {showMoreOptions && (
                  <motion.div
                    initial={{ opacity:0, height:0 }}
                    animate={{ opacity:1, height:'auto' }}
                    exit={{ opacity:0, height:0 }}
                    transition={{ duration:0.2 }}
                    style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {orgFeatures?.emergency_trainer && onStartEmergency ? (
                      <motion.button type="button" onClick={() => onStartEmergency(selectedAircraft)}
                        whileHover={{ scale:1.02 }} whileTap={{ scale:0.96 }}
                        style={{ width:'100%', padding:'12px', border:'none', borderRadius:16, cursor:'pointer', fontFamily:'inherit', background:'linear-gradient(135deg,#ef4444,#f97316)', color:'white', boxShadow:'0 4px 14px rgba(239,68,68,0.3)', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                        <span style={{ fontSize:16 }}>🚨</span>
                        <span style={{ fontSize:13, fontWeight:700 }}>{t('emergency_simulator')}</span>
                      </motion.button>
                    ) : (onUpsell && (userRole === 'admin' || userRole === 'instructor')) ? (
                      <motion.button type="button" onClick={() => onUpsell('emergency_trainer')}
                        whileHover={{ scale:1.02 }} whileTap={{ scale:0.96 }}
                        style={{ width:'100%', padding:'12px', border:'1px solid rgba(100,116,139,0.2)', borderRadius:16, cursor:'pointer', fontFamily:'inherit', background:'rgba(100,116,139,0.12)', color:'#94a3b8', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                        <span style={{ fontSize:16 }}>🔒</span>
                        <span style={{ fontSize:13, fontWeight:700 }}>{t('emergency_simulator')}</span>
                      </motion.button>
                    ) : null}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Flow Mode unlock banner ── */}
        <AnimatePresence>
          {selectedAircraft && !groundedAircraft.has(selectedAircraft?.id) && orgFeatures?.flow_mode && !currentUser?.flow_mode_enabled && (currentUser?.total_inspections_completed || 0) >= 10 && (
            <motion.div key="flow-unlock-banner"
              initial={{ opacity:0, height:0 }}
              animate={{ opacity:1, height:'auto' }}
              exit={{ opacity:0, height:0 }}
              style={{ marginTop:8 }}>
              <div style={{ padding:'10px 14px', borderRadius:'14px', background:'rgba(129,140,248,0.06)', border:'1px solid rgba(129,140,248,0.15)', display:'flex', alignItems:'center', gap:'8px' }}>
                <span style={{ fontSize:'16px' }}>🌀</span>
                <p style={{ margin:0, fontSize:'12px', color:'#818cf8', fontWeight:600, lineHeight:1.4 }}>
                  {t('flow_mode_unlock')}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {selectedAircraft && (
            <motion.div initial={{ opacity: 0, y: 8, height: 0 }} animate={{ opacity: 1, y: 0, height: 'auto' }} exit={{ opacity: 0, y: 4, height: 0 }} transition={{ duration: 0.2 }}
              style={{ marginTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              <CheckCircle style={{ width: '13px', height: '13px', color: groundedAircraft.has(selectedAircraft.id) ? '#ef4444' : c1 }} />
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>
                <span style={{ color: groundedAircraft.has(selectedAircraft.id) ? '#ef4444' : c1 }}>{selectedAircraft.name}</span>
                {' '}{groundedAircraft.has(selectedAircraft.id) ? t('aog_active') : t('selected')}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </form>

      <AnimatePresence>
        {showInstallBanner && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }}
            transition={{ type: 'spring', stiffness: 200, damping: 22 }}
            style={{ margin: '0 20px 20px', padding: '14px 16px', borderRadius: '16px', background: 'linear-gradient(135deg, rgba(37,99,235,0.08), rgba(109,40,217,0.06))', border: '1px solid rgba(37,99,235,0.18)', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '42px', height: '42px', borderRadius: '12px', flexShrink: 0, background: 'linear-gradient(135deg, #2563eb, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', boxShadow: '0 4px 12px rgba(37,99,235,0.3)' }}>📲</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: '13px', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.2px' }}>{t('install_app_title')}</p>
              <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#64748b', fontWeight: 500 }}>{t('install_app_desc')}</p>
            </div>
            <motion.button onClick={handleInstall} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
              style={{ padding: '8px 14px', borderRadius: '10px', flexShrink: 0, background: 'linear-gradient(135deg, #2563eb, #7c3aed)', color: 'white', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer', boxShadow: '0 3px 10px rgba(37,99,235,0.35)', fontFamily: 'inherit' }}>
              {t('install')}
            </motion.button>
            <motion.button onClick={handleDismissInstall} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
              style={{ width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0, background: 'rgba(100,116,139,0.12)', border: 'none', color: '#94a3b8', cursor: 'pointer', fontWeight: 700, fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit' }}>
              ×
            </motion.button>
          </motion.div>
        )}
        {showManualTip && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }}
            transition={{ type: 'spring', stiffness: 200, damping: 22 }}
            style={{ margin: '0 20px 20px', padding: '14px 16px', borderRadius: '16px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', position: 'relative' }}>
            <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 800, color: '#92400e' }}>📲 {t('install_how_title')}</p>
            <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#78350f', lineHeight: 1.5 }}>
              <strong>Android Chrome:</strong> {t('install_android')}
            </p>
            <p style={{ margin: 0, fontSize: '12px', color: '#78350f', lineHeight: 1.5 }}>
              <strong>iPhone Safari:</strong> {t('install_ios')}
            </p>
            <motion.button onClick={() => setShowManualTip(false)} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
              style={{ position: 'absolute', top: '10px', right: '12px', width: '24px', height: '24px', borderRadius: '50%', background: 'rgba(100,116,139,0.12)', border: 'none', color: '#94a3b8', cursor: 'pointer', fontWeight: 700, fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit' }}>
              ×
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <DebugPanel selectedAircraftId={selectedAircraft?.id} />
    </motion.div>
  );
});

AircraftSelector.displayName = 'AircraftSelector';
export default AircraftSelector;
